import React, { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../contexts/AuthContext';

const API_BASE_URL = window.API_BASE_URL;

const ACTION_LABEL = {
  SORT_SAVED: 'Sort saved',
  SORT_REVIEWED: 'Sort reviewed',
  CHECK_FINISHED: 'Check finished',
  GRADE_STARTED: 'Check started',
  GRADE_STARTED_DIRECT: 'Check started',
  APPROVED: 'Approved',
  MARKS_UPDATED: 'Marks updated',
  MARKS_DELETED: 'Marks deleted',
  RESULTS_FINALIZED: 'Results finalized',
  RUBRIC_CREATED: 'Rubric created',
  RUBRIC_UPDATED: 'Rubric edited',
  RUBRIC_DELETED: 'Rubric deleted',
  RUBRIC_ACTIVATED: 'Rubric activated',
  RUBRIC_GENERATED: 'Rubric generated',
  STUDENT_LIST_IMPORTED: 'List imported',
  STUDENT_LIST_DELETED: 'List deleted',
  STUDENT_ADDED: 'Student added',
  STUDENT_DELETED: 'Student deleted',
  SCAN_DELETED: 'Image deleted',
  OCR_EDITED: 'OCR edited',
  CURRICULUM_GENERATED: 'Plans generated'
};

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function isoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function actionLabel(action) {
  if (ACTION_LABEL[action]) return ACTION_LABEL[action];
  return String(action || 'Task').replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
}

function sumActions(bag, keys) {
  if (!bag) return 0;
  return keys.reduce((n, key) => n + (bag[key] || 0), 0);
}

function personTotal(actions) {
  return Object.values(actions || {}).reduce((n, v) => n + v, 0);
}

export default function WorkLogTab() {
  const { currentUser } = useAuth();
  const today = useMemo(() => new Date(), []);
  const [cursor, setCursor] = useState(() => ({ year: today.getFullYear(), month: today.getMonth() }));
  const [selected, setSelected] = useState(() => isoDate(today));
  const [teacher, setTeacher] = useState('');
  const [seeAll, setSeeAll] = useState(false);
  const [users, setUsers] = useState([]);
  const [days, setDays] = useState({});
  const [totals, setTotals] = useState({});
  const [logs, setLogs] = useState([]);
  const [loadingMonth, setLoadingMonth] = useState(false);
  const [loadingDay, setLoadingDay] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoadingMonth(true);
      try {
        const token = localStorage.getItem('authToken');
        const params = new URLSearchParams({
          year: String(cursor.year),
          month: String(cursor.month + 1)
        });
        if (teacher) params.set('username', teacher);
        const res = await fetch(`${API_BASE_URL}/work-log/calendar?${params}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled || !res.ok) return;
        setSeeAll(!!data.seeAll);
        setDays(data.days || {});
        setTotals(data.totals || {});
      } catch (err) {
        console.error('[WorkLog] calendar', err);
      } finally {
        if (!cancelled) setLoadingMonth(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [cursor.year, cursor.month, teacher]);

  useEffect(() => {
    if (!seeAll) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('authToken');
        const res = await fetch(`${API_BASE_URL}/work-log/users`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json().catch(() => ({}));
        if (!cancelled && res.ok) setUsers(data.users || []);
      } catch (err) {
        console.error('[WorkLog] users', err);
      }
    })();
    return () => { cancelled = true; };
  }, [seeAll]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoadingDay(true);
      try {
        const token = localStorage.getItem('authToken');
        const params = new URLSearchParams({ date: selected });
        if (teacher) params.set('username', teacher);
        const res = await fetch(`${API_BASE_URL}/work-log/day?${params}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled || !res.ok) return;
        setLogs(data.logs || []);
      } catch (err) {
        console.error('[WorkLog] day', err);
      } finally {
        if (!cancelled) setLoadingDay(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [selected, teacher]);

  const cells = useMemo(() => {
    const first = new Date(cursor.year, cursor.month, 1);
    const start = new Date(first);
    start.setDate(1 - first.getDay());
    const out = [];
    for (let i = 0; i < 42; i += 1) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      out.push({
        date,
        key: isoDate(date),
        inMonth: date.getMonth() === cursor.month
      });
    }
    return out;
  }, [cursor.year, cursor.month]);

  const monthTotals = useMemo(() => {
    const bag = {};
    Object.values(totals).forEach((actions) => {
      Object.entries(actions).forEach(([action, n]) => {
        bag[action] = (bag[action] || 0) + n;
      });
    });
    return bag;
  }, [totals]);

  const summary = [
    { label: 'Sorted', n: monthTotals.SORT_SAVED || 0 },
    { label: 'Reviewed', n: monthTotals.SORT_REVIEWED || 0 },
    { label: 'Checks', n: sumActions(monthTotals, ['CHECK_FINISHED', 'GRADE_STARTED', 'GRADE_STARTED_DIRECT']) },
    { label: 'Approved', n: monthTotals.APPROVED || 0 }
  ];

  const monthTitle = new Date(cursor.year, cursor.month, 1).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric'
  });
  const selectedLabel = new Date(`${selected}T12:00:00`).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });

  const shiftMonth = (delta) => {
    setCursor((prev) => {
      const next = new Date(prev.year, prev.month + delta, 1);
      return { year: next.getFullYear(), month: next.getMonth() };
    });
  };

  const teacherLabel = (u) => {
    if (u.email && u.email !== u.username) return `${u.username} · ${u.email}`;
    return u.username;
  };

  return (
    <div style={{
      height: 'calc(100vh - var(--header-height, 52px) - 28px)',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      padding: '4px 6px 0'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0, marginBottom: 8 }}>
        <h1 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 750 }}>Work log</h1>
        <span style={{ color: 'var(--text-secondary, #64748b)', fontSize: '0.88rem' }}>
          {seeAll ? 'Daily tasks on the grader' : `${currentUser?.username || 'You'} · your tasks`}
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          <button type="button" onClick={() => shiftMonth(-1)} aria-label="Previous month" style={navBtn}>
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          <strong style={{ minWidth: 140, textAlign: 'center' }}>{monthTitle}</strong>
          <button type="button" onClick={() => shiftMonth(1)} aria-label="Next month" style={navBtn}>
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, flexShrink: 0, marginBottom: 10 }}>
        {summary.map((item) => (
          <div key={item.label} style={chip}>
            <span style={{ color: 'var(--text-secondary, #64748b)', fontSize: 12 }}>{item.label}</span>
            <strong>{loadingMonth ? '…' : item.n}</strong>
          </div>
        ))}
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: seeAll ? 'minmax(0, 1.4fr) minmax(260px, 0.8fr) 280px' : 'minmax(0, 1.4fr) minmax(280px, 0.8fr)',
        gap: 14,
        alignItems: 'stretch',
        flex: 1,
        minHeight: 0
      }}>
        <div style={{ ...card, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginBottom: 4, flexShrink: 0 }}>
            {WEEKDAYS.map((name) => (
              <div key={name} style={{ textAlign: 'center', fontSize: 11, fontWeight: 700, color: 'var(--text-secondary, #64748b)' }}>
                {name}
              </div>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gridTemplateRows: 'repeat(6, minmax(0, 1fr))', gap: 4, flex: 1, minHeight: 0 }}>
            {cells.map((cell) => {
              const people = days[cell.key] || {};
              const names = Object.keys(people);
              const total = names.reduce((n, name) => n + personTotal(people[name]), 0);
              const isToday = cell.key === isoDate(today);
              const isSelected = cell.key === selected;
              return (
                <button
                  key={cell.key}
                  type="button"
                  onClick={() => setSelected(cell.key)}
                  style={{
                    textAlign: 'left',
                    minHeight: 0,
                    overflow: 'hidden',
                    padding: '4px 6px',
                    borderRadius: 8,
                    border: isSelected ? '2px solid #2563EB' : '1px solid var(--border, #E5E7EB)',
                    background: isSelected ? 'rgba(37,99,235,0.08)' : 'var(--card-bg, #fff)',
                    opacity: cell.inMonth ? 1 : 0.45,
                    cursor: 'pointer'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{
                      fontWeight: 700,
                      fontSize: 12,
                      color: isToday ? '#2563EB' : 'var(--text-primary)'
                    }}
                    >
                      {cell.date.getDate()}
                    </span>
                    {total > 0 && (
                      <span style={{ fontSize: 10, fontWeight: 700, color: '#1d4ed8' }}>{total}</span>
                    )}
                  </div>
                  {names.slice(0, 2).map((name) => (
                    <div key={name} style={{ fontSize: 10, lineHeight: 1.3, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {teacher ? actionLabel(topAction(people[name])) : name}
                      {' · '}
                      {personTotal(people[name])}
                    </div>
                  ))}
                  {names.length > 2 && (
                    <div style={{ fontSize: 10, color: 'var(--text-secondary, #64748b)' }}>+{names.length - 2}</div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div style={{ ...card, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <h2 style={{ margin: '0 0 8px', fontSize: '0.95rem', flexShrink: 0 }}>{selectedLabel}</h2>
          {loadingDay && <p style={{ color: 'var(--text-secondary)' }}>Loading…</p>}
          {!loadingDay && logs.length === 0 && (
            <p style={{ color: 'var(--text-secondary)', margin: 0 }}>No tasks recorded this day.</p>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1, minHeight: 0, overflowY: 'auto' }}>
            {logs.map((log) => (
              <div key={log.id} style={{ borderTop: '1px solid var(--border, #E5E7EB)', paddingTop: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <strong style={{ fontSize: 13 }}>{actionLabel(log.action)}</strong>
                  <span style={{ fontSize: 11, color: 'var(--text-secondary, #64748b)', whiteSpace: 'nowrap' }}>
                    {log.created_at ? new Date(log.created_at).toLocaleTimeString('en-PK', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Karachi' }) : ''}
                  </span>
                </div>
                {seeAll && !teacher && (
                  <div style={{ fontSize: 12, fontWeight: 650, color: '#1d4ed8' }}>{log.username}</div>
                )}
                {log.details && (
                  <div style={{ fontSize: 12, color: 'var(--text-primary)', marginTop: 2 }}>{log.details}</div>
                )}
              </div>
            ))}
          </div>
        </div>

        {seeAll && (
          <div style={{ ...card, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <h2 style={{ margin: '0 0 8px', fontSize: '0.95rem', flexShrink: 0 }}>Teachers</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minHeight: 0, overflowY: 'auto' }}>
              <button type="button" onClick={() => setTeacher('')} style={teacherRow(teacher === '')}>
                <span>All teachers</span>
              </button>
              {users.map((u) => {
                const n = personTotal(totals[u.username]);
                return (
                  <button
                    key={u.username}
                    type="button"
                    onClick={() => setTeacher(u.username)}
                    style={teacherRow(teacher === u.username)}
                  >
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{teacherLabel(u)}</span>
                    {n > 0 && <span style={{ fontSize: 11, fontWeight: 700, color: '#1d4ed8', flexShrink: 0 }}>{n}</span>}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function topAction(actions) {
  let best = '';
  let bestN = -1;
  Object.entries(actions || {}).forEach(([action, n]) => {
    if (n > bestN) {
      best = action;
      bestN = n;
    }
  });
  return best;
}

const navBtn = {
  width: 32,
  height: 32,
  borderRadius: 8,
  border: '1px solid var(--border, #E5E7EB)',
  background: 'var(--card-bg, #fff)',
  cursor: 'pointer'
};

const chip = {
  display: 'flex',
  flexDirection: 'column',
  minWidth: 88,
  padding: '8px 12px',
  borderRadius: 10,
  border: '1px solid var(--border, #E5E7EB)',
  background: 'var(--card-bg, #fff)'
};

const card = {
  background: 'var(--card-bg, #fff)',
  border: '1px solid var(--border, #E5E7EB)',
  borderRadius: 12,
  padding: 12
};

function teacherRow(active) {
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    width: '100%',
    textAlign: 'left',
    padding: '8px 10px',
    borderRadius: 8,
    border: active ? '2px solid #2563EB' : '1px solid transparent',
    background: active ? 'rgba(37,99,235,0.08)' : 'transparent',
    color: 'var(--text-primary)',
    fontWeight: active ? 700 : 600,
    fontSize: 13,
    cursor: 'pointer'
  };
}
